export type ToolKind = "widget" | "server";
export type ToolDomain = "intake" | "lookup" | "memory" | "visit" | "routing" | "system";
export type ToolAvailability = "core" | "mid" | "checkout";

export type DataClassification = "ui_reflective";

export type ToolMetadata = {
  kind: ToolKind;
  domain: ToolDomain;
  availability: ToolAvailability;
  defaultIncluded?: boolean;
  autoSubmit?: boolean;
  /** Widget with server execute that still needs user interaction before continuing. */
  interactive?: boolean;
  legacyAliases?: string[];
  /** Data classification for filtering/export behavior. */
  dataClassification?: DataClassification;
};

export const TOOL_METADATA = {
  signal: {
    kind: "server",
    domain: "system",
    availability: "core",
    defaultIncluded: true,
    dataClassification: "ui_reflective",
    legacyAliases: ["signal_status", "signal_insight"],
  },
  save_intake_fields: {
    kind: "server",
    domain: "intake",
    availability: "core",
    defaultIncluded: true,
  },
  get_intake_status: {
    kind: "server",
    domain: "intake",
    availability: "core",
    defaultIncluded: true,
  },
  intake_navigate: {
    kind: "server",
    domain: "intake",
    availability: "core",
    legacyAliases: ["intake_go_back", "intake_edit_field", "intake_back", "intake_edit"],
  },
  get_patient: { kind: "server", domain: "intake", availability: "core", defaultIncluded: true },
  get_categories: {
    kind: "server",
    domain: "routing",
    availability: "core",
    defaultIncluded: true,
  },
  switch_category: {
    kind: "server",
    domain: "routing",
    availability: "core",
    defaultIncluded: true,
  },
  check_eligibility: { kind: "server", domain: "intake", availability: "core" },
  search_catalog: {
    kind: "server",
    domain: "lookup",
    availability: "core",
    defaultIncluded: true,
    legacyAliases: [
      "lookup_product",
      "lookup_clinical",
      "lookup_catalog",
      "lookup_labs",
      "lookup_hair",
      "lookup_skin",
      "lookup_ed",
      "lookup_menopause",
      "lookup_mental_health",
      "lookup_sexual_health",
      "lookup_birth_control",
    ],
  },
  get_filtered_offers: { kind: "server", domain: "lookup", availability: "mid" },
  lookup_claim: { kind: "server", domain: "lookup", availability: "core" },
  lookup_testimonial: { kind: "server", domain: "lookup", availability: "mid" },
  save_memory: {
    kind: "server",
    domain: "memory",
    availability: "core",
    defaultIncluded: true,
    legacyAliases: ["manage_memory"],
  },
  search_memory: { kind: "server", domain: "memory", availability: "mid" },
  save_artifact: { kind: "server", domain: "memory", availability: "core" },
  visit: {
    kind: "server",
    domain: "visit",
    availability: "core",
    legacyAliases: ["visit_start", "visit_submit", "visit_status", "visit_finalize"],
  },
  show_form: {
    kind: "widget",
    domain: "intake",
    availability: "core",
    defaultIncluded: true,
    legacyAliases: ["render_json_form"],
  },
  show_product: {
    kind: "widget",
    domain: "lookup",
    availability: "core",
    defaultIncluded: true,
    legacyAliases: ["render_product_card"],
  },
  show_progress: {
    kind: "widget",
    domain: "intake",
    availability: "mid",
    autoSubmit: true,
    legacyAliases: ["render_progress"],
  },
  show_auth: {
    kind: "widget",
    domain: "routing",
    availability: "core",
    defaultIncluded: true,
    legacyAliases: ["render_auth_widget"],
  },
  show_consent: {
    kind: "widget",
    domain: "intake",
    availability: "core",
    legacyAliases: ["render_telehealth_consent"],
  },
  show_claim: { kind: "widget", domain: "lookup", availability: "core", autoSubmit: true },
  show_testimonial: {
    kind: "widget",
    domain: "lookup",
    availability: "mid",
    autoSubmit: true,
    legacyAliases: ["render_testimonial"],
  },
  show_pricing: {
    kind: "widget",
    domain: "lookup",
    availability: "mid",
    legacyAliases: ["render_pricing_breakdown"],
  },
  show_comparison: {
    kind: "widget",
    domain: "lookup",
    availability: "mid",
    legacyAliases: ["render_product_comparison"],
  },
  show_bmi: {
    kind: "widget",
    domain: "intake",
    availability: "core",
    autoSubmit: true,
    legacyAliases: ["render_bmi_result"],
  },
  show_chips: {
    kind: "widget",
    domain: "intake",
    availability: "core",
    legacyAliases: ["render_chips"],
  },
  show_chip_group: {
    kind: "widget",
    domain: "intake",
    availability: "core",
  },
  show_intake_summary: {
    kind: "widget",
    domain: "intake",
    availability: "mid",
    autoSubmit: true,
    legacyAliases: ["render_intake_summary"],
  },
  show_checkout: {
    kind: "widget",
    domain: "visit",
    availability: "checkout",
    legacyAliases: [
      "show_shipping_address",
      "show_payment",
      "show_ssn",
      "show_order_confirmation",
      "show_dob_verification",
      "show_photo_upload",
      "render_shipping_address",
      "render_payment_method",
      "render_ssn_verification",
      "render_order_confirmation",
      "render_photo_upload",
    ],
  },
  show_weight_input: {
    kind: "widget",
    domain: "intake",
    availability: "core",
  },
  show_reasoning_trace: {
    kind: "widget",
    domain: "system",
    availability: "core",
    autoSubmit: true,
  },
  show_patient_profile: {
    kind: "widget",
    domain: "system",
    availability: "core",
    autoSubmit: true,
  },
  show_text_input: {
    kind: "widget",
    domain: "intake",
    availability: "core",
  },
  show_structured_input: {
    kind: "widget",
    domain: "intake",
    availability: "core",
  },
  show_meducation: {
    kind: "widget",
    domain: "lookup",
    availability: "mid",
    interactive: true,
  },
} as const satisfies Record<string, ToolMetadata>;

export type ToolName = keyof typeof TOOL_METADATA;

const LEGACY_ALIAS_LOOKUP = Object.entries(TOOL_METADATA).reduce<Record<string, ToolName>>(
  (aliases, [toolName, metadata]) => {
    for (const alias of (metadata as ToolMetadata).legacyAliases ?? []) {
      aliases[alias] = toolName as ToolName;
    }
    return aliases;
  },
  {},
);

export function resolveToolName(name: string): ToolName | string {
  return (TOOL_METADATA as Record<string, ToolMetadata>)[name]
    ? (name as ToolName)
    : (LEGACY_ALIAS_LOOKUP[name] ?? name);
}

export function getToolMetadata(name: string): ToolMetadata | undefined {
  return TOOL_METADATA[resolveToolName(name) as ToolName];
}

export function isWidgetTool(name: string): boolean {
  return getToolMetadata(name)?.kind === "widget";
}

export function isAutoSubmitWidget(name: string): boolean {
  return getToolMetadata(name)?.autoSubmit === true;
}

export function isInteractiveWidget(name: string): boolean {
  return getToolMetadata(name)?.interactive === true;
}

export function isLookupTool(name: string): boolean {
  return getToolMetadata(name)?.domain === "lookup";
}

/**
 * Build a sanitized eval hint from widget input for browser eval automation.
 * Only included in __EVAL_MODE__ — returns the full input since eval sessions
 * use synthetic data with no real PII.
 */
export function buildEvalHint(
  _toolName: string,
  input: Record<string, unknown>,
): Record<string, unknown> {
  return input;
}

export function normalizeToolPartType(type: string): string {
  if (!type.startsWith("tool-")) return `tool-${resolveToolName(type)}`;
  return `tool-${resolveToolName(type.slice("tool-".length))}`;
}

export function getToolNameFromPart(part: { toolName?: unknown; type?: unknown }): string | null {
  if (typeof part.toolName === "string") {
    return String(resolveToolName(part.toolName));
  }

  if (typeof part.type === "string" && part.type.startsWith("tool-")) {
    return String(resolveToolName(part.type.slice("tool-".length)));
  }

  return null;
}

export function isWidgetToolPart(part: { toolName?: unknown; type?: unknown }): boolean {
  const toolName = getToolNameFromPart(part);
  return toolName ? isWidgetTool(toolName) : false;
}
